import React, { useState } from "react";
import { CreditCard, X, Loader2, FlaskConical } from "lucide-react";
import { paymentsAPI } from "../api";
import "./ConfirmModal.css";
import "./PaymentModal.css";

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  order, 
  onPaymentSuccess 
}) => {
  const [paymentMethod, setPaymentMethod] = useState("ALL");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await paymentsAPI.initiate(order.id, {
        phone_number: phoneNumber || undefined,
        payment_method: paymentMethod
      });

      if (response.data.payment_url) {
        // Rediriger vers la page de paiement CinetPay
        window.location.href = response.data.payment_url;
      } else {
        setError("Erreur lors de l'initialisation du paiement");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'initialisation du paiement");
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSimulatePayment = async () => {
    setError("");
    setSimulating(true);

    try {
      const response = await paymentsAPI.simulate(order.id);
      
      if (response.data.success) {
        // Appeler le callback de succès pour recharger la commande
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
        onClose();
      } else {
        setError("Erreur lors de la simulation du paiement");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la simulation du paiement");
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content confirm-modal payment-modal">
        <div className="modal-header">
          <h2 className="modal-title icon-inline"><CreditCard size={19} strokeWidth={2.25} /> Paiement de la commande</h2>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X size={20} strokeWidth={2.25} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="payment-amount-section">
            <div className="payment-amount-label">Montant à payer</div>
            <div className="payment-amount-value">
              {order.total_price.toLocaleString("fr-FR")} F CFA
            </div>
            <div className="payment-order-info">
              Commande #{order.id} - {order.product.name}
            </div>
          </div>

          {error && (
            <div className="alert alert-error" style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="payment_method" className="form-label">
                Méthode de paiement *
              </label>
              <select
                id="payment_method"
                className="form-control"
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  if (e.target.value !== "MOBILE_MONEY") {
                    setPhoneNumber("");
                  }
                }}
                required
              >
                <option value="ALL">Toutes les méthodes</option>
                <option value="MOBILE_MONEY">Mobile Money (Orange Money, MTN, Moov)</option>
                <option value="CARD">Carte bancaire (Visa, Mastercard)</option>
              </select>
            </div>

            {paymentMethod === "MOBILE_MONEY" || paymentMethod === "ALL" ? (
              <div className="form-group">
                <label htmlFor="phone_number" className="form-label">
                  Numéro de téléphone (Mobile Money)
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  className="form-control"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Ex: +221 77 123 45 67"
                  pattern="[+]?[0-9\s-]+"
                />
                <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                  Optionnel si vous choisissez "Toutes les méthodes". Requis pour Mobile Money uniquement.
                </p>
              </div>
            ) : null}

            <div className="modal-footer" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={onClose}
                  disabled={loading || simulating}
                  style={{ flex: 1 }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || simulating}
                  style={{ flex: 1, minWidth: "150px" }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} strokeWidth={2.25} className="spin" style={{ marginRight: "0.5rem" }} />
                      Initialisation...
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} strokeWidth={2.25} style={{ marginRight: "0.5rem" }} />
                      Payer maintenant
                    </>
                  )}
                </button>
              </div>
              
              {/* Bouton de simulation, uniquement en développement local (jamais en build de prod) */}
              {process.env.NODE_ENV === "development" && (
                <button
                  type="button"
                  className="btn btn-simulate"
                  onClick={handleSimulatePayment}
                  disabled={loading || simulating}
                  style={{
                    width: "100%",
                    fontSize: "0.9rem"
                  }}
                >
                  {simulating ? (
                    <>
                      <Loader2 size={16} strokeWidth={2.25} className="spin" style={{ marginRight: "0.5rem" }} />
                      Simulation...
                    </>
                  ) : (
                    <>
                      <FlaskConical size={16} strokeWidth={2.25} style={{ marginRight: "0.5rem" }} />
                      Simuler le paiement (Test)
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;

